/*
    Examiner's rater invite page.

    PIN-210. This page was offered on every battery, including performance-based
    ones, where the resulting link opens an examiner-driven adaptive test for an
    unauthenticated respondent — it renders, but nothing about it works. Only
    questionnaire tests are on offer now, and the page is named for what it does.
*/

import React, { useCallback, useEffect, useState } from 'react';
import Dashboard from '../dashboard';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import styles from '../../style/info-card.module.css';
import { fetchBatteryTests, isInvitableTest } from '../../utils/battery-kind';
import { useStartSession } from '../../hooks/useStartSession';
import { Button, Input, Select, notification } from 'antd';

const SessionInvitePage = () => {
  const { batteryID } = useParams();
  const history = useHistory();
  const token = useSelector((state) => state.token);
  const { isStarting, start } = useStartSession();

  const [examinees, setExaminees] = useState([]);
  const [isSendingInvite, setIsSendingInvite] = useState(false);
  const [selectedExamineeID, setSelectedExamineeID] = useState(null);
  const [selectedTestID, setSelectedTestID] = useState(null);
  const [selectedTestMode, setSelectedTestMode] = useState(null);
  const [selectedLabel, setSelectedLabel] = useState('');
  const [inviteEmails, setInviteEmails] = useState('');
  const [invitePath, setInvitePath] = useState('');
  const [tests, setTests] = useState([]);
  const [userInfo, setUserInfo] = useState({});

  // PIN-199 #2: the Record Label is required here (unlike the examinee Label,
  // which is optional). A whitespace-only label used to pass this check and
  // produce a URL with a blank path segment.
  const hasRecordLabel = selectedLabel.trim().length > 0;

  /*
    Held as a path rather than a full URL so "Administer Now" can push it into
    the router; the origin is prepended only for the copyable invite field.
  */
  const createInvitePath = useCallback(() => {
    // The label is free text. Unencoded, a '#' or '/' in it truncates the path —
    // silently corrupting the copied invite URL.
    let path = `/examinee/startTest/${encodeURIComponent(
      userInfo._id
    )}/${encodeURIComponent(selectedLabel)}/${encodeURIComponent(
      selectedTestID
    )}/${encodeURIComponent(selectedExamineeID)}`;
    if (selectedTestMode === 'questionnaire') path += '?mode=questionnaire';
    if (!selectedLabel.trim() || !selectedTestID || !selectedExamineeID) path = '';
    setInvitePath(path);
  }, [selectedExamineeID, selectedLabel, selectedTestID, selectedTestMode, userInfo]);

  const sessionInviteURL = invitePath ? `${window.location.origin}${invitePath}` : '';

  /*
    Catching the axios rejection and then dereferencing response.data threw a
    TypeError inside an un-awaited effect — an unhandled rejection, with the
    examinee Select silently empty and nothing to tell the examiner why.
  */
  const getExaminees = useCallback(async () => {
    try {
      const response = await axios.get(process.env.REACT_APP_API_URL + '/examinee/directory', {
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Token ' + token
        }
      });

      if (!response || !response.data || !response.data.examinees)
        throw new Error('Could not get examinee directory.');
      setExaminees(response.data.examinees);
    } catch (error) {
      console.log(error);
      notification['error']({
        message: 'Could not load examinees.',
        description: 'Reload the page to try again.'
      });
    }
  }, [token]);

  // Shares the deduped fetch with the side menu, which needs the same list on
  // the same view; the same unhandled-rejection trap lived here too.
  const getTests = useCallback(async () => {
    const { tests: batteryTests, failed } = await fetchBatteryTests(batteryID, token);
    if (failed) {
      notification['error']({
        message: "Could not load this battery's tests.",
        description: 'Reload the page to try again.'
      });
    }
    setTests(batteryTests);
  }, [batteryID, token]);

  const getUserInfo = useCallback(async () => {
    const config = {
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Token ' + token
      }
    };

    let response = await axios
      .post(process.env.REACT_APP_API_URL + '/user/info', {}, config)
      .catch((error) => {
        console.log(error);
      });

    setUserInfo(response.data);
  }, [token]);

  const renderExamineeSelect = useCallback(() => {
    return (
      <Select
        allowClear
        className={styles['item-select']}
        filterOption
        onChange={setSelectedExamineeID}
        optionFilterProp="filtervalue"
        placeholder="Examinee"
        showSearch
      >
        {Object.values(examinees).map((examinee) => {
          return (
            <Select.Option
              filtervalue={`${examinee.firstName} ${examinee.lastName} ${examinee.label}`}
              key={examinee._id}
            >
              {`${examinee.firstName} ${examinee.lastName}`}
            </Select.Option>
          );
        })}
      </Select>
    );
  }, [examinees]);

  const handleTestChange = useCallback(
    (testID) => {
      setSelectedTestID(testID);
      const test = tests.find((t) => t._id === testID);
      setSelectedTestMode(test ? test.mode || 'adaptive' : null);
    },
    [tests]
  );

  const formatTestName = (name) => name.replace(/\((\d+-\d+)\)/g, '(Ages $1)');

  const renderTestSelect = useCallback(() => {
    return (
      <Select
        allowClear
        className={styles['item-select']}
        filterOption
        onChange={handleTestChange}
        optionFilterProp="filtervalue"
        placeholder="Test"
        showSearch
      >
        {/* Only a rating scale can be completed by a rater from a link; a
            performance-based test needs an examiner-administered session. */}
        {Object.values(tests)
          .filter(isInvitableTest)
          .map((test) => {
            return (
              <Select.Option filtervalue={test.name} key={test._id}>
                {formatTestName(test.name)}
              </Select.Option>
            );
          })}
      </Select>
    );
  }, [handleTestChange, tests]);

  /*
    PIN-231: the API builds and sends the email, so the link in it can only point
    at this app. _silent keeps the error middleware's generic toast from stacking
    on top of the one below, which carries the API's reason.
  */
  const sendInvite = useCallback(async () => {
    setIsSendingInvite(true);
    try {
      await axios.post(
        process.env.REACT_APP_API_URL + '/session/invite',
        {
          examineeID: selectedExamineeID,
          testID: selectedTestID,
          recordLabel: selectedLabel,
          emails: inviteEmails
            .split(',')
            .map((email) => email.trim())
            .filter(Boolean)
        },
        {
          headers: { 'Content-Type': 'application/json', Authorization: 'Token ' + token },
          _silent: true
        }
      );
      notification['success']({ message: 'Invite sent.' });
      setInviteEmails('');
    } catch (error) {
      notification['error']({
        message: 'Could not send invite.',
        description: error.response?.data?.message || 'Please try again.'
      });
    } finally {
      setIsSendingInvite(false);
    }
  }, [inviteEmails, selectedExamineeID, selectedLabel, selectedTestID, token]);

  const administerNow = useCallback(async () => {
    const started = await start({
      examineeID: selectedExamineeID,
      recordLabel: selectedLabel,
      testID: selectedTestID
    });

    // A null result means the hook already surfaced the failure and kept the
    // selections intact.
    if (started) history.push(`/examiner/questionnaire/${started.sessionID}`);
  }, [history, selectedExamineeID, selectedLabel, selectedTestID, start]);

  useEffect(() => {
    getExaminees();
    getTests();
    getUserInfo();
  }, [getExaminees, getTests, getUserInfo]);

  useEffect(() => {
    createInvitePath();
  }, [createInvitePath, selectedExamineeID, selectedLabel, selectedTestID]);

  return (
    <Dashboard>
      <div className={styles['card']}>
        <div className={styles['header']}>
          <label className={styles['name']}>Invite Rater</label>
        </div>
        <hr className={styles['horizontal-line']}></hr>
        <div className={styles['info-container']}>
          <div className={styles['item-container']}>
            <label className={styles['item-name']}>Examinee</label>
            {renderExamineeSelect()}
            <hr className={styles['item-underline']}></hr>
          </div>
          <div className={styles['item-container']}>
            <label className={styles['item-name']}>Test</label>
            {renderTestSelect()}
            <hr className={styles['item-underline']}></hr>
          </div>
          <div className={styles['item-container']}>
            <label className={styles['item-name']}>
              Record Label
              {/*
                Without this the requirement was invisible: a blank label just
                left the Invite URL empty, which reads as a broken page rather
                than as missing input (PIN-199 #2).
              */}
              {!hasRecordLabel && (
                <span className={styles['required-indicator']} data-testid="record-label-required">
                  {' '}
                  (Required)
                </span>
              )}
            </label>
            <Input
              allowClear
              className={styles['item-select']} /* Apply the same class as Select components */
              onChange={(event) => setSelectedLabel(event.target.value)}
              onPressEnter={(e) => e.target.blur()}
              placeholder="Label"
              value={selectedLabel}
            />
            <hr className={styles['item-underline']}></hr>
          </div>
          <div className={styles['break']} />
          <div className={styles['item-container']}>
            <label className={styles['item-name']}>Invite URL</label>
            <Input
              className={styles['item-select']} /* Apply the same class as Select components */
              readOnly={true}
              value={sessionInviteURL}
            />
            <hr className={styles['item-underline']}></hr>
          </div>
          <div className={styles['item-container']}>
            {/*
              The generic Session route is gone (PIN-210), so this is how an
              examiner completes a rating scale themselves rather than sending it
              out. It creates a real examiner session rather than opening the
              invite link: /session/generate marks its sessions hidden, and
              hidden sessions are filtered out of the examiner's session list —
              so an examiner who took the invite path and navigated away could
              never get back to it.
            */}
            <Button
              disabled={!selectedExamineeID || !selectedLabel || !selectedTestID}
              loading={isStarting}
              onClick={administerNow}
              type="primary"
            >
              Administer Now
            </Button>
          </div>
          <div className={styles['break']} />
          <div className={styles['item-container']}>
            <label className={styles['item-name']}>Email Invite</label>
            <Input
              className={styles['item-select']}
              onChange={(event) => setInviteEmails(event.target.value)}
              placeholder="Email(s), comma separated"
              value={inviteEmails}
            />
            <hr className={styles['item-underline']}></hr>
          </div>
          <div className={styles['item-container']}>
            <Button
              disabled={!invitePath || !inviteEmails.trim()}
              loading={isSendingInvite}
              onClick={sendInvite}
              type="primary"
            >
              Send
            </Button>
          </div>
        </div>
      </div>
    </Dashboard>
  );
};

export default SessionInvitePage;
